// generateSearchResultsPageJsonLd.js
'use strict';

import { getI18nInstance } from '../resource/i18n.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import {
  DEFAULT_LANGUAGE,
  localeConvertor,
  PREFERRED_LOCALES,
} from '../resource/i18nCommon.js';
import { getLanguageTranslationKey } from '../resource/getCurrentLanguage.js';
import { searchPageTDKTranslateKeys } from '../resource/searchConstants.js';

/**
 * Generate SearchResultsPage JSON-LD for search pages
 * @param {Object} params
 * @param {string} params.query - Search query string
 * @param {string} params.type - Search type (all, flix, swagger, story, hashtag)
 * @param {string} params.url - Full URL of the search page
 * @param {Array<string>} params.supportedLanguages - Array of supported language codes
 * @returns {Array} Array of JSON-LD objects [WebPage, BreadcrumbList]
 */
const generateSearchResultsPageJsonLd = async ({
  query = '',
  type = 'all',
  url = '',
  supportedLanguages = PREFERRED_LOCALES,
}) => {
  const { i18n } = getI18nInstance();

  const alternateLanguages = supportedLanguages.filter(
    language => language !== DEFAULT_LANGUAGE
  );

  const loadLanguagePromises = alternateLanguages.map(async lang => {
    if (!i18n.hasResourceBundle(lang, TranslationNamespace.SEARCH)) {
      await i18n.reloadResources(lang, TranslationNamespace.SEARCH);
    }
  });
  await Promise.all(loadLanguagePromises);

  // Get TDK translation keys based on search type
  const tdkKeys =
    searchPageTDKTranslateKeys[type] || searchPageTDKTranslateKeys.default;

  // Helper functions (following getUserProfileJsonLd pattern)
  const localeToBCP47 = lang =>
    localeConvertor({ locale: lang, isISO639: true });

  const buildNameValueByLang = (lang, value) => ({
    '@value': value,
    '@language': localeToBCP47(lang),
  });

  // Generate title and description for a specific language
  const getTitleByLang = lang => {
    const baseTitle = i18n.t(tdkKeys.title, {
      lng: lang,
      ns: TranslationNamespace.SEARCH,
    });
    return query ? `${query} - ${baseTitle}` : baseTitle;
  };

  const getDescriptionByLang = lang => {
    const baseDescription = i18n.t(tdkKeys.description, {
      lng: lang,
      ns: TranslationNamespace.SEARCH,
    });
    return query ? `${query} - ${baseDescription}` : baseDescription;
  };

  // Base WebPage schema
  const webPage = {
    '@type': 'WebPage',
    '@id': `${url}#webpage`,
    url,
    inLanguage: supportedLanguages.map(lang => ({
      '@type': 'Language',
      name: i18n.t(getLanguageTranslationKey({ language: lang }), {
        ns: TranslationNamespace.SEO,
      }),
      alternateName: localeToBCP47(lang),
    })),
    name: buildNameValueByLang(
      DEFAULT_LANGUAGE,
      getTitleByLang(DEFAULT_LANGUAGE)
    ),
    alternateName: alternateLanguages.map(lang =>
      buildNameValueByLang(lang, getTitleByLang(lang))
    ),
    description: buildNameValueByLang(
      DEFAULT_LANGUAGE,
      getDescriptionByLang(DEFAULT_LANGUAGE)
    ),
    alternateDescription: alternateLanguages.map(lang =>
      buildNameValueByLang(lang, getDescriptionByLang(lang))
    ),
    breadcrumb: {
      '@id': `${url}#breadcrumb`,
    },
  };

  // BreadcrumbList schema
  const breadcrumbList = {
    '@type': 'BreadcrumbList',
    '@id': `${url}#breadcrumb`,
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: buildNameValueByLang(
          DEFAULT_LANGUAGE,
          i18n.t('seo-home-title', {
            lng: DEFAULT_LANGUAGE,
            ns: TranslationNamespace.SEO,
          })
        ),
        alternateName: alternateLanguages.map(lang =>
          buildNameValueByLang(
            lang,
            i18n.t('seo-home-title', {
              lng: lang,
              ns: TranslationNamespace.SEO,
            })
          )
        ),
        item: {
          '@id': 'https://swag.live/#website',
        },
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: buildNameValueByLang(
          DEFAULT_LANGUAGE,
          getTitleByLang(DEFAULT_LANGUAGE)
        ),
        alternateName: alternateLanguages.map(lang =>
          buildNameValueByLang(lang, getTitleByLang(lang))
        ),
        item: {
          '@id': `${url}#webpage`,
        },
      },
    ],
  };

  // Return array format (not wrapped in @context/@graph)
  return [webPage, breadcrumbList];
};

export default generateSearchResultsPageJsonLd;
